import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';
import Icon from '../Icon';
import Typo from '../Typo';
import { getUserThemeContrastColor, getDarkerThemeColor, getLighterThemeColor } from '../theme/colors';
import CircularProgress from '../CircularProgress';

const ButtonComponent = ({
  onClick,
  children,
  loading,
  $asLink,
  $asText,
  trackEvent,
  startIcon,
  endIcon,
  isLoading = false, // Prop for loading state
  progressValue = 0, // Prop for progress percentage
  ...props
}) => {
  const { disabled } = props;

  const handleClick = useCallback(
    (event) => {
      if (onClick) {
        onClick(event);
      }
      if (trackEvent) {
        // track(trackEvent);
      }
    },
    [onClick, trackEvent],
  );

  if ('href' in props && props.href && !disabled && !loading && !isLoading) {
    const { href, newTab, ...rest } = props;
    const handleLinkClick = (event) => {
      handleClick(event);
      if (href && !href.startsWith('http') && !event.isDefaultPrevented()) {
        event.preventDefault();
      }
    };

    const target = newTab ? '_blank' : undefined;
    const rel = newTab ? 'noreferrer noopener' : undefined;

    return (
      <a onClick={handleLinkClick} href={href} target={target} rel={rel} {...rest}>
        <Typo.Info>{children}</Typo.Info>
      </a>
    );
  }

  const beforeIcon = props.icon || startIcon;
  // Use a circular progress indicator as the start icon when isLoading is true
  const StartIconComponent = isLoading ? () => <CircularProgress value={progressValue} size={20} /> : beforeIcon && Icon[beforeIcon];
  const EndIconComponent = endIcon && Icon[endIcon];

  return (
    <button onClick={handleClick} aria-busy={isLoading} aria-label={isLoading ? `Downloading (${progressValue}%)` : props['aria-label'] || 'Button'} {...props}>
      {StartIconComponent && <StartIconComponent size='small' />}
      <Typo.MediumText fontWeight='500' $textAlign='center'>
        {children}
      </Typo.MediumText>
      {!isLoading && EndIconComponent && <EndIconComponent size='small' />}
    </button>
  );
};

const Button = styled(ButtonComponent).attrs(({ loading, disabled, ...props }) => {
  const href = 'href' in props ? props.href : undefined;

  return {
    disabled: loading === true || props.isLoading ? true : disabled,
    href: (disabled || loading || props.isLoading) && href ? undefined : href,
    loading,
  };
})`
  justify-content: center;
  background-color: ${(props) => props.theme.colors.PRIMARY};
  border: 1px solid transparent;
  color: ${(props) => props.theme.colors.NEU_0};
  border-radius: 22px;
  cursor: pointer;
  padding: 10px 15px;
  min-width: 2.75rem;
  display: flex;
  gap: 8px;
  min-width: 120px;
  transition: all 0.15s ease;

  p {
    color: ${(props) => props.theme.colors.NEU_0};
    text-align: left;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.PRIMARY_LIGHT};
    border-color: transparent;
    text-decoration: none;
    color: ${(props) => props.theme.colors.NEU_PRIMARY};
    p {
      color: ${(props) => props.theme.colors.NEU_PRIMARY};
    }
  }

  &:focus {
    z-index: 1;
    text-decoration: none;
    outline: none;
    border-color: transparent;
    box-shadow: ${(props) => props.theme.shadows.BOX_SHADOW};
  }

  &:disabled,
  &[aria-disabled='true'] {
    background-color: ${(props) => props.theme.colors.NEU_50};
    border-color: ${(props) => props.theme.colors.NEU_50};
    cursor: not-allowed;
    color: ${(props) => props.theme.colors.NEU_200};
    p {
      color: ${(props) => props.theme.colors.NEU_200};
    }
  }

  ${({ $variant }) =>
    $variant === 'action' &&
    css`
      background-color: ${(props) => props.theme.colors.ACTION};
      color: ${(props) => props.theme.colors.NEU_0};
      border-color: ${(props) => props.theme.colors.ACTION};
      p {
        color: ${(props) => props.theme.colors.NEU_0};
      }

      &:hover {
        background-color: transparent;
        color: ${(props) => props.theme.colors.ACTION};
        p {
          color: ${(props) => props.theme.colors.ACTION};
        }
        border-color: ${(props) => props.theme.colors.ACTION};
      }
    `}

  ${({ $variant }) =>
    $variant === 'square' &&
    css`
      border-radius: 0;
      align-items: center;
      background-color: ${(props) => props.$bgcolor || props.theme.colors.NEU_PRIMARY};
      border-color: ${(props) => props.$bgcolor || props.theme.colors.NEU_PRIMARY};
      text-decoration: none;
      color: ${(props) => (props.$bgcolor ? getUserThemeContrastColor(props.$bgcolor) : props.theme.colors.NEU_0)};
      p {
        color: ${(props) => (props.$bgcolor ? getUserThemeContrastColor(props.$bgcolor) : props.theme.colors.NEU_0)};
      }
      &:hover {
        background-color: ${(props) => props.$hoverColor || 'transparent'};
        color: ${(props) => (props.$hoverColor ? getUserThemeContrastColor(props.$hoverColor) : props.theme.colors.NEU_PRIMARY)};
        p {
          color: ${(props) => (props.$hoverColor ? getUserThemeContrastColor(props.$hoverColor) : props.theme.colors.NEU_PRIMARY)};
        }
        border-color: ${(props) => (props.$hoverColor ? props.$hoverColor : props.theme.colors.NEU_PRIMARY)};
      }

      &:disabled,
      &[aria-disabled='true'] {
        background-color: ${(props) => props.theme.colors.NEU_50};
        border-color: ${(props) => props.theme.colors.NEU_50};
        color: ${(props) => props.theme.colors.NEU_200};
        p {
          color: ${(props) => props.theme.colors.NEU_200};
        }
      }
    `}

  ${({ $variant }) =>
    $variant === 'customized' &&
    css`
      background-color: ${(props) => props.$bgcolor || props.theme.colors.NEU_PRIMARY};
      border-color: ${(props) => props.$bgcolor || props.theme.colors.NEU_PRIMARY};
      text-decoration: none;
      color: ${(props) => (props.$bgcolor ? getUserThemeContrastColor(props.$bgcolor) : props.theme.colors.NEU_0)};
      p {
        color: ${(props) => (props.$bgcolor ? getUserThemeContrastColor(props.$bgcolor) : props.theme.colors.NEU_0)};
      }

      &:hover {
        background-color: ${(props) => props.$hoverColor || 'transparent'};
        color: ${(props) => (props.$hoverColor ? getUserThemeContrastColor(props.$hoverColor) : props.theme.colors.NEU_PRIMARY)};
        p {
          color: ${(props) => (props.$hoverColor ? getUserThemeContrastColor(props.$hoverColor) : props.theme.colors.NEU_PRIMARY)};
        }
        border-color: ${(props) => (props.$hoverColor ? props.$hoverColor : props.theme.colors.NEU_PRIMARY)};
      }

      &:disabled,
      &[aria-disabled='true'] {
        background-color: ${(props) => props.theme.colors.NEU_50};
        border-color: ${(props) => props.theme.colors.NEU_50};
        color: ${(props) => props.theme.colors.NEU_200};
        p {
          color: ${(props) => props.theme.colors.NEU_200};
        }
      }
    `}

  ${({ $variant }) =>
    $variant === 'primary' &&
    css`
      background-color: ${(props) => props.theme.colors.NEU_PRIMARY};
      border-color: ${(props) => props.theme.colors.NEU_PRIMARY};
      text-decoration: none;
      color: ${(props) => props.theme.colors.NEU_0};
      p {
        color: ${(props) => props.theme.colors.NEU_0};
      }

      &:hover {
        background-color: transparent;
        color: ${(props) => props.theme.colors.NEU_PRIMARY};
        p {
          color: ${(props) => props.theme.colors.NEU_PRIMARY};
        }
        border-color: ${(props) => props.theme.colors.NEU_PRIMARY};
      }

      &:disabled,
      &[aria-disabled='true'] {
        background-color: ${(props) => props.theme.colors.NEU_50};
        border-color: ${(props) => props.theme.colors.NEU_50};
        color: ${(props) => props.theme.colors.NEU_200};
        p {
          color: ${(props) => props.theme.colors.NEU_200};
        }
      }
    `}

  ${({ $variant }) =>
    $variant === 'themed' &&
    css`
      background-color: ${(props) => (props.$themeColor ? getLighterThemeColor(props.$themeColor) : props.theme.colors.PRIMARY_LIGHT)};
      border-color: ${(props) => (props.$themeColor ? getLighterThemeColor(props.$themeColor) : props.theme.colors.PRIMARY_LIGHT)};
      text-decoration: none;
      color: ${(props) => (props.$themeColor ? getDarkerThemeColor(props.$themeColor) : props.theme.colors.PRIMARY)};
      p {
        color: ${(props) => (props.$themeColor ? getDarkerThemeColor(props.$themeColor) : props.theme.colors.PRIMARY)};
      }

      &:hover {
        background-color: ${(props) => (props.$themeColor ? getDarkerThemeColor(props.$themeColor) : props.theme.colors.PRIMARY)};
        border-color: ${(props) => (props.$themeColor ? props.theme.colors.NEU_0 : props.theme.colors.PRIMARY)};
        color: ${(props) => props.theme.colors.NEU_0};
        p {
          color: ${(props) => props.theme.colors.NEU_0};
        }
      }

      &:disabled,
      &[aria-disabled='true'] {
        background-color: ${(props) => props.theme.colors.NEU_50};
        border-color: ${(props) => props.theme.colors.NEU_50};
        color: ${(props) => props.theme.colors.NEU_200};
        p {
          color: ${(props) => props.theme.colors.NEU_200};
        }
      }
    `}

  ${({ $variant }) =>
    $variant === 'basic' &&
    css`
      background-color: ${(props) => props.theme.colors.NEU_0};
      color: ${(props) => props.theme.colors.NEU_PRIMARY};
      p {
        color: ${(props) => props.theme.colors.NEU_PRIMARY};
      }
      &:hover {
        background-color: ${(props) => props.theme.colors.NEU_PRIMARY};
        color: ${(props) => props.theme.colors.NEU_0};
        p {
          color: ${(props) => props.theme.colors.NEU_0};
        }
      }
    `}

  ${({ $asText }) =>
    $asText &&
    css`
      background: none;
      border: none;
      box-shadow: none;
      color: ${(props) => props.theme.colors.NEU_PRIMARY};
      padding: 0;
      text-decoration: underline;
      min-width: 0;

      p {
        font-weight: 500;
        font-size: ${(props) => props.theme.fontSizes.small};
        color: ${(props) => props.theme.colors.NEU_PRIMARY};
      }

      &:hover {
        background: none;
        border: none;
        color: ${(props) => props.theme.colors.PRIMARY};
        p {
          color: ${(props) => props.theme.colors.PRIMARY};
        }
      }

      &:focus,
      &:active {
        box-shadow: none;
        color: ${(props) => props.theme.colors.NEU_PRIMARY};
        p {
          color: ${(props) => props.theme.colors.NEU_PRIMARY};
        }
      }
    `}

  ${({ $asLink }) =>
    $asLink &&
    css`
      background: none;
      border: none;
      box-shadow: none;
      color: ${(props) => props.theme.colors.NEU_PRIMARY};
      font-weight: 300;
      p {
        font-weight: 300;
        color: ${(props) => props.theme.colors.NEU_PRIMARY};
      }
      padding: 0;
      text-decoration: none;

      &:hover {
        background: none;
        border: none;
        text-decoration: underline;
        color: ${(props) => props.theme.colors.NEU_PRIMARY};
        p {
          color: ${(props) => props.theme.colors.NEU_PRIMARY};
        }
      }

      &:focus,
      &:active {
        box-shadow: none;
        color: ${(props) => props.theme.colors.NEU_PRIMARY};
        p {
          color: ${(props) => props.theme.colors.NEU_PRIMARY};
        }
      }

      &:disabled,
      &[aria-disabled='true'] {
        background: none;
        cursor: not-allowed;
        color: ${(props) => props.theme.colors.NEU_200};
        p {
          color: ${(props) => props.theme.colors.NEU_200};
        }
        &:hover {
          text-decoration: none;
        }
      }
    `}
`;

export default Button;
